import type { JobDetail, JobOpeningStatus } from '@nest-recruit/shared';
import type { Tone } from '../../components/Tone';
import { formatIsoDate, formatNumber } from '../../lib/format';

// Workflow review D1 (DECISIONS-LOG 2026-11-01, D1-a): the colourful numbers beside a job's title. How many days it has
// been running and the deadline are worked out here from the job's own dates, never stored (#10); Applied is counted by
// the server. Days are calendar days in the organisation's zone, so "today" is the same day the person sees on the wall.

/** From this many days left, the deadline needs attention. */
export const DEADLINE_SOON_DAYS = 7;

const MS_PER_DAY = 86_400_000;
const ENDED_STATUSES: readonly JobOpeningStatus[] = ['filled', 'closed_partial', 'cancelled'];

export interface SummaryTile {
  readonly label: string;
  readonly value: string;
  /** The small line under the value; null when there is nothing more to say. */
  readonly meta: string | null;
  readonly tone: Tone;
}

type SummaryJob = Pick<JobDetail, 'status' | 'openedAt' | 'closedAt' | 'targetCloseDate'>;

/** `YYYY-MM-DD` of an instant in a zone. */
export function calendarDayOf(instant: Date | string, timeZone: string): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((each) => each.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** Whole calendar days from one `YYYY-MM-DD` to another; negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}

function daysWord(days: number): string {
  return `${formatNumber(days)} ${days === 1 ? 'day' : 'days'}`;
}

export function isEnded(status: JobOpeningStatus): boolean {
  return ENDED_STATUSES.includes(status);
}

/** "Running 12 days since 20-10-2026"; a draft is not open yet; an ended job says how long it ran. */
export function runningTileOf(job: SummaryJob, today: string, timeZone: string): SummaryTile {
  if (job.status === 'draft' || job.openedAt === null) return { label: 'Running', value: 'Not open yet', meta: null, tone: 'new' };
  const opened = calendarDayOf(job.openedAt, timeZone);
  if (isEnded(job.status)) {
    const closed = job.closedAt === null ? today : calendarDayOf(job.closedAt, timeZone);
    return { label: 'Ran', value: daysWord(Math.max(0, daysBetween(opened, closed))), meta: `${formatIsoDate(opened)} to ${formatIsoDate(closed)}`, tone: 'ended' };
  }
  const days = Math.max(0, daysBetween(opened, today));
  return {
    label: 'Running',
    value: days === 0 ? 'Opened today' : daysWord(days),
    meta: `since ${formatIsoDate(opened)}`,
    tone: job.status === 'on_hold' ? 'hold' : 'live',
  };
}

/** The target close date and how far off it is; "No deadline" when none is set (D1-a). */
export function deadlineTileOf(job: SummaryJob, today: string): SummaryTile & { readonly isMissing: boolean } {
  if (job.targetCloseDate === null) return { label: 'Deadline', value: 'No deadline', meta: null, tone: 'new', isMissing: true };
  const date = formatIsoDate(job.targetCloseDate);
  if (isEnded(job.status)) return { label: 'Deadline', value: date, meta: null, tone: 'ended', isMissing: false };
  const left = daysBetween(today, job.targetCloseDate);
  if (left < 0) return { label: 'Deadline', value: `${daysWord(-left)} over`, meta: date, tone: 'urgent', isMissing: false };
  if (left === 0) return { label: 'Deadline', value: 'Due today', meta: date, tone: 'attention', isMissing: false };
  return { label: 'Deadline', value: `${daysWord(left)} left`, meta: date, tone: left <= DEADLINE_SOON_DAYS ? 'attention' : 'progress', isMissing: false };
}

/** "Applied 103"; the area tone of screening, where applicants are worked through. */
export function appliedTileOf(applied: number | undefined): SummaryTile {
  return { label: 'Applied', value: formatNumber(applied), meta: null, tone: 'screening' };
}
